import { toast } from "react-toastify";

export const handleErrorMessages = (error, defaultMessage = "An unknown error occurred.") => {
    let message = error;

    if (error?.response?.data) {
        message = error.response.data.message || error.response.data.error || error.response.data;
    } else if (error?.message) {
        message = error.message;
    }

    if (typeof message === "string") {
        return message;
    } else if (typeof message === "object" && message !== null) {
        Object.values(message).forEach((err) => {
            if (Array.isArray(err)) {
                err.forEach((msg) => toast.error(msg));
            } else {
                return err;
            }
        });
    } else {
        return defaultMessage;
    }
};

export const maskEmail = (email) => {
    const [localPart, domain] = email.split('@');
    if (localPart.length <= 3) {
        return email;
    }
    const firstTwo = localPart.slice(0, 2);
    const lastChar = localPart.slice(-4);
    const maskedPart = '*'.repeat(localPart.length - 3);
    return `${firstTwo}${maskedPart}${lastChar}@${domain}`;
};

export const formatDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

export const formatDateRev = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${day}/${month}/${year}`;
};

export const formatAmountFixed = (amount) => {
    const formattedAmount = Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(((parseFloat(amount || 0)).toFixed(2)));

    return formattedAmount;
}

export const formatAmountFixedAbr = (amount) => {
    const value = parseFloat(amount || 0);

    if (Math.abs(value) >= 100000) {
        const units = [
            { value: 1e12, suffix: 'T' },
            { value: 1e9, suffix: 'B' },
            { value: 1e6, suffix: 'M' },
            { value: 1e3, suffix: 'K' },
        ];

        const unit = units.find(u => Math.abs(value) >= u.value);

        const formatted = (value / unit.value)
            .toFixed(2);
            // .replace(/\.?0+$/, '');

        return `₦${formatted}${unit.suffix}`;
    }

    return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
};

export const toSentenceCase = (str) => {
    const words = str.split('_');
    const capitalizedWords = words.map((word, index) => {
        if (index === 0) {
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        } else {
            return word.toLowerCase();
        }
    });
    return capitalizedWords.join(' ');
}

export const capitalizeWords = (str) => {
    return str.replaceAll('_', ' ').replace(/\b\w/g, char => char.toUpperCase());
};

export const validateFieldValue = (key, value, fieldValidation, label) => {
    const trimmedValue = value?.toString().trim();

    const capitalizeFirstLetter = (str) => str.charAt(0).toUpperCase() + str.slice(1);
    const fieldLabel = capitalizeFirstLetter(label || key.replace(/_/g, ' '));

    if (fieldValidation.required && !trimmedValue) {
        return `${fieldLabel} is required.`;
    } else if (fieldValidation.type === 'number' && isNaN(trimmedValue)) {
        return `${fieldLabel} must be a valid number.`;
    } else if (
        fieldValidation.type === 'phone' && trimmedValue &&
        !/^(\+?[1-9]\d{6,14}|0\d{10})$/.test(trimmedValue)
    ) {
        return `${fieldLabel} must be a valid phone number.`;
    } else if (
        fieldValidation.type === 'email' && trimmedValue &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)
    ) {
        return `${fieldLabel} must be a valid email.`;
    } else if (
        fieldValidation.type === 'link' && trimmedValue &&
        !/^https?:\/\/[^\s$.?#].[^\s]*$/.test(trimmedValue)
    ) {
        return `${fieldLabel} must be a valid link.`;
    } else if (fieldValidation.min && trimmedValue.length > 0 && trimmedValue.length < fieldValidation.min) {
        return `${fieldLabel} must be at least ${fieldValidation.min} characters long.`;
    } else if (fieldValidation.max && trimmedValue.length > 0 && trimmedValue.length > fieldValidation.max) {
        return `${fieldLabel} must be no more than ${fieldValidation.max} characters long.`;
    }

    return null;
};

export const validateValue = (label, value, type, required = false, min, max) => {
    const trimmedValue = value?.toString().trim();

    const capitalizeFirstLetter = (str) => str.charAt(0).toUpperCase() + str.slice(1);
    const fieldLabel = capitalizeFirstLetter(label || 'Field');

    if (required && !trimmedValue) {
        return `${fieldLabel} is required.`;
    } else if (type === 'number' && isNaN(trimmedValue)) {
        return `${fieldLabel} must be a valid number.`;
    } else if (type === 'phone' && trimmedValue && !/^(\+?[1-9]\d{6,14}|0\d{10})$/.test(trimmedValue)) {
        return `${fieldLabel} must be a valid phone number.`;
    } else if (type === 'email' && trimmedValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)) {
        return `${fieldLabel} must be a valid email.`;
    } else if (type === 'date' && trimmedValue && isNaN(Date.parse(trimmedValue))) {
        return `${fieldLabel} must be a valid date.`;
    } else if (type === 'link' && trimmedValue && !/^https?:\/\/[^\s$.?#].[^\s]*$/.test(trimmedValue)) {
        return `${fieldLabel} must be a valid link.`;
    } else if (type === 'nin' && trimmedValue && !/^\d{11}$/.test(trimmedValue)) {
        return `${fieldLabel} must be exactly 11 digits.`;
    } else if (type === 'bvn' && trimmedValue && !/^\d{11}$/.test(trimmedValue)) {
        return `${fieldLabel} must be exactly 10 digits.`;
    } else if (min && trimmedValue.length < min) {
        return `${fieldLabel} must be at least ${min} characters long.`;
    } else if (max && trimmedValue.length > max) {
        return `${fieldLabel} must be no more than ${max} characters long.`;
    }

    return null;
};

